import React from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ContactWarningPreference } from "@/enum/ContactWarningPreference";
import { useGetOwnMembershipQuery, useUpdateOwnContactWarningPreferenceMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

export default function ContactWarningCard() {
  const { data: membership, isLoading } = useGetOwnMembershipQuery();
  const [updatePreference, { isLoading: isSaving }] = useUpdateOwnContactWarningPreferenceMutation();

  const isWarningOn = membership?.contactWarningPreference !== ContactWarningPreference.SUPPRESSED;

  async function onToggle(checked: boolean) {
    try {
      await updatePreference({
        contactWarningPreference: checked
          ? ContactWarningPreference.SHOW
          : ContactWarningPreference.SUPPRESSED,
      }).unwrap();
      toast.success(checked ? "Consent warnings turned on" : "Consent warnings turned off");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update your consent warnings"));
    }
  }

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consent warnings</CardTitle>
        <CardDescription>
          What happens before you call or text a number with nothing on file.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-4 rounded-md border p-3">
          <div className="flex-1 space-y-1">
            <span className="block text-sm font-semibold">Warn me before contacting without consent</span>
            <span className="block text-sm text-muted-foreground">
              Shows the warning before the call or text goes out, with the option to check the number
              against a Do Not Call list first.
            </span>
          </div>
          <Switch
            checked={isWarningOn}
            disabled={isSaving}
            onCheckedChange={onToggle}
            aria-label="Warn me before contacting without consent"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Numbers found on a Do Not Call or litigator list stay blocked whichever way this is set.
        </p>
      </CardContent>
    </Card>
  );
}
